import { computed, type Ref } from 'vue';
import type { CartItem } from '@cvo/ui/components/domain/CartSubscription/CartSubscription.model';
import { isFixedCartItem, type AllowedCartItem, type PercentageCartItem, type FixedCartItem, type DefaultCartMeta, type CartItemTypes } from '../types/cart';
import { useCartCalculator } from './useCartCalculator';

/**
 * A mapper that transforms raw cart items into a display-ready format for the CartSubscription component.
 * It prepares the items for the UI by formatting prices and adjusting properties.
 *
 * @param items - A ref to the array of cart items.
 * @returns An object with the display-ready items.
 */
export function useCartDisplayItems<TExtra extends DefaultCartMeta = DefaultCartMeta>(items: Ref<Array<AllowedCartItem<TExtra>>>) {
  const { formatPrice } = usePriceFormatter({ trailingZeroDisplay: 'auto' });
  const { calculateItemTotalAmount } = useCartCalculator(items);

  function getFixedItemValue(item: FixedCartItem<TExtra>): string {
    if (typeof item.value === 'string') {
      return item.value;
    }

    return formatPrice({ amount: item.value.toString(), currency: item.currency });
  }

  function getPercentageItemValue(item: PercentageCartItem<TExtra>): string {
    const amount = calculateItemTotalAmount(item);
    return formatPrice({ amount: amount.toString(), currency: item.currency });
  }

  const displayValueStrategy = {
    fixed: getFixedItemValue,
    percentage: getPercentageItemValue,
  } satisfies {
    [K in CartItemTypes]: (item: Extract<AllowedCartItem<TExtra>, { type: K }>) => string;
  };

  const displayItems = computed((): CartItem[] => {
    return items.value.map((item) => {
      // @ts-expect-error - This is a known TS limitation with discriminated unions in complex maps.
      const value = displayValueStrategy[item.type](item);

      return {
        ...item,
        quantity: isFixedCartItem(item) ? item.quantity : null,
        value,
        originalValue: item.originalValue !== undefined
          ? formatPrice({ amount: item.originalValue.toString(), currency: item.currency })
          : undefined,
      };
    });
  });

  return {
    displayItems,
  };
}
